const constants = require('constants');
const forge = require('node-forge');
const { Binary } = require('binary');
const Promise = require('bluebird');
const precond = require('precond');
const utils = require('../utils');
const cryptoConfig = require('./crypto-config');
const cryptoUtils = require('./crypto-utils');
const Algorithms = require('../ciphers/algorithms');
const errors = require('./errors');

let crypto;

try {
  if (typeof window === 'undefined') {
    crypto = require('crypto');
  }
} catch (err) {
  /** Ignore errors */
}

/**
 * Encrypt content.
 * @param {Binary} payload - The payload to encrypt
 * @param {Binary} key     - The encryption key
 * @param {Binary} iv      - The initialization vector
 * @param {String} [algorithm] - The algorithm to use for encryption
 * @return {Object} - The encrypted output
 * {
 *   payload: {Binary} - The encrypted payload.
 *   [authTag]: {Binary} The authentication tag generated.
 * }
 * @property
 */

function encrypt(payload, key, iv, algorithm) {
  precond.checkIsObject(payload);
  precond.checkIsObject(key);
  precond.checkIsObject(iv);

  const alg = selectAlgorithm(algorithm);
  if (alg === 'aes-256-gcm') {
    return utils.isStream(payload)
      ? _doStreamingGcmEncrypt(payload, key, iv)
      : _doGcmEncryptSync(payload, key, iv);
  }

  // CBC
  return utils.isStream(payload)
    ? _doStreamingCrypt('createCipheriv', payload, key, iv)
    : cbcCrypt('createCipheriv', payload, key, iv);
}

/**
 * Decrypt content
 * @param {Binary} payload - The payload to decrypt
 * @param {Binary} key     - The encryption key
 * @param {Binary} iv      - The initialization vector
 * @param {String} [algorithm] - The algorithm to use for encryption
 * @param {Binary} [authTag] - The authentication tag for authenticated crypto.
 * @return {Object} - The decrypted output
 * {
 *   payload: {Binary} - The decrypted payload.
 * }
 * @property
 */
function decrypt(payload, key, iv, algorithm, authTag) {
  precond.checkIsObject(payload);
  precond.checkIsObject(key);
  precond.checkIsObject(iv);

  const alg = selectAlgorithm(algorithm);
  if (alg === 'aes-256-gcm') {
    precond.checkIsObject(authTag);
    return utils.isStream(payload)
      ? _doStreamingGcmDecrypt(payload, key, iv, authTag)
      : _doGcmDecryptSync(payload, key, iv, authTag);
  }

  // CBC
  return utils.isStream(payload)
    ? _doStreamingCrypt('createDecipheriv', payload, key, iv, algorithm)
    : cbcCrypt('createDecipheriv', payload, key, iv, algorithm);
}

/**
 * Decrypt a public-key encrypted payload with a private key
 * @param  {Binary} encryptedPayload - Payload to decrypt
 * @param  {String} privateKey       - PEM formatted private key
 * @return {Promise<Binary>}         - Decrypted payload
 */
function decryptWithPrivateKey(encryptedPayload, privateKey) {
  precond.checkIsObject(encryptedPayload);
  precond.checkIsString(privateKey);

  return new Promise(function decryptWithPrivateKeyPromise(resolve, reject) {
    const key = {
      key: privateKey,
      padding: constants.RSA_PKCS1_OAEP_PADDING,
    };
    const payload = encryptedPayload.asBuffer().toString('base64');
    const cleartext = crypto.privateDecrypt(key, Buffer.from(payload, 'base64'));
    return resolve(Binary.fromBuffer(cleartext));
  });
}

function _doStreamingCrypt(method, stream, key, iv, algorithm) {
  const cryptoStream = crypto[method]('aes-256-cbc', key.asBuffer(), iv.asBuffer());

  stream.pipe(cryptoStream);

  return Promise.resolve({
    payload: cryptoStream,
  });
}

function _doStreamingGcmEncrypt(stream, key, iv) {
  const cryptoStream = crypto.createCipheriv('aes-256-gcm', key.asBuffer(), iv.asBuffer());

  stream.pipe(cryptoStream);

  return Promise.resolve({
    payload: cryptoStream,
  });
}

function _doStreamingGcmDecrypt(stream, key, iv, authTag) {
  const cryptoStream = crypto.createDecipheriv('aes-256-gcm', key.asBuffer(), iv.asBuffer());

  cryptoStream.setAuthTag(authTag.asBuffer());
  stream.pipe(cryptoStream);

  return Promise.resolve({
    payload: cryptoStream,
  });
}

function cbcCrypt(method, payload, key, iv) {
  return new Promise(function promise(resolve, reject) {
    try {
      const cryptoStream = crypto[method]('aes-256-cbc', key.asBuffer(), iv.asBuffer());

      const data = cryptoStream.update(payload.asBuffer());
      const final = cryptoStream.final();

      resolve({
        payload: Binary.fromBuffer(Buffer.concat([data, final])),
      });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Encrypts synchronously using GCM
 * @param payload
 * @param key
 * @param iv
 * @private
 */
function _doGcmEncryptSync(payload, key, iv) {
  return new Promise(function promise(resolve, reject) {
    try {
      const cryptoStream = crypto.createCipheriv('aes-256-gcm', key.asBuffer(), iv.asBuffer());

      const data = cryptoStream.update(payload.asBuffer());
      const final = cryptoStream.final();

      const result = {
        payload: Binary.fromBuffer(Buffer.concat([data, final])),
        authTag: Binary.fromBuffer(cryptoStream.getAuthTag()),
      };
      resolve(result);
    } catch (err) {
      reject(err);
    }
  });
}

function _doGcmDecryptSync(payload, key, iv, authTag) {
  return new Promise(function promise(resolve, reject) {
    try {
      const cryptoStream = crypto.createDecipheriv('aes-256-gcm', key.asBuffer(), iv.asBuffer());

      cryptoStream.setAuthTag(authTag.asBuffer());
      const data = cryptoStream.update(payload.asBuffer());
      const final = cryptoStream.final();

      return resolve({
        payload: Binary.fromBuffer(Buffer.concat([data, final])),
      });
    } catch (err) {
      if (err.message.includes('unable to authenticate data')) {
        return reject(errors.IntegrityCompromisedError.create(err));
      }

      return reject(err);
    }
  });
}

function selectAlgorithm(algorithm) {
  if (algorithm === Algorithms.AES_256_GCM) {
    return 'aes-256-gcm';
  }
  return 'aes-256-cbc';
}

function randomBytesAsHex(size) {
  return crypto.randomBytes(size).toString('hex');
}

function generateInitializationVector(length) {
  return randomBytesAsHex(length || 16);
}

function generateKey(length) {
  return randomBytesAsHex(length || 32);
}

/**
 * Encrypt using a public key.
 * Returns a promise to be consistent with the
 * promise-based browser native implementation.
 * @param  {Binary} payload   - Payloat to encrypt
 * @param  {String} publicKey - PEM formatted public key
 * @return {Promise<Binary>}  - Encrypted payload
 */
function encryptWithPublicKey(payload, publicKey) {
  precond.checkIsObject(payload);
  precond.checkIsString(publicKey);

  return new Promise(function encryptWithPublicKeyPromise(resolve, reject) {
    const key = {
      key: publicKey,
      padding: constants.RSA_PKCS1_OAEP_PADDING,
    };
    const ciphertext = crypto.publicEncrypt(key, Buffer.from(payload.asBuffer()));
    return resolve(Binary.fromBuffer(ciphertext));
  });
}

/**
 * Generate an RSA key pair. The forge module is used instead
 * of the native crypto module because the latter does not
 * generate RSA keys (at least it's not documented as of Node v4)
 *
 * @param  {Number} [size] in bits
 * @return {Promise<Object>} Promise of:
 *   {
 *     publicKey: PEM,
 *     privateKey: PEM
 *   }
 */
function generateKeyPair(size) {
  const minKeySize = cryptoConfig.getMinAsymmetricKeySizeBits();

  if (!cryptoUtils.isValidAsymmetricKeySize(size, minKeySize)) {
    throw new Error('Invalid key size requested');
  }

  return new Promise(function promise(resolve, reject) {
    const { pki } = forge;
    const { rsa } = pki;
    const keySize = !size ? minKeySize : size;

    // TODO: Get async approach working after moving off of forge fork
    const keys = rsa.generateKeyPair({ bits: keySize, e: 0x10001 });
    return resolve({
      publicKey: pki.publicKeyToPem(keys.publicKey),
      privateKey: pki.privateKeyToPem(keys.privateKey),
    });
  });
}

/**
 * Create a SHA256 hash
 * @param  {String}  content  String content
 * @return {String}  hash     Hex hash
 */
function sha256(content) {
  const hash = crypto.createHash('sha256');
  hash.update(content, 'binary');
  return Promise.resolve(hash.digest('hex'));
}

/**
 * Create an HMAC SHA256 hash
 * @param  {String} key     Key string
 * @param  {String} content Content string
 * @return {Promise<String>} Hex hash
 */
function hmac(key, content) {
  const decoded = Buffer.from(key, 'hex');

  const hmacObj = crypto.createHmac('sha256', decoded);

  hmacObj.update(content);
  const digest = hmacObj.digest('hex');

  return Promise.resolve(digest);
}

module.exports = {
  encrypt,
  decrypt,
  encryptWithPublicKey,
  decryptWithPrivateKey,
  generateKey,
  generateKeyPair,
  generateInitializationVector,
  isSupported: crypto !== undefined,
  sha256,
  hmac,
  randomBytesAsHex,
  name: 'NodeCryptoService',
};
